'use client'

import { useEffect } from 'react'
import { useCookieState } from '@dustin-web/microsite-components/src/hooks/use-cookie-state'
import { OptimizelyDecideOption, useDecide } from '@optimizely/react-sdk'
import { type TransformedFeatureFlag, transformFeatureFlag } from '../utils/feature-flag-transform'
import { sendFeatureFlagToGTM } from '../utils/feature-flag-gtm'

/**
 * Custom hook for managing feature flag decisions with Optimizely, cookie transformation,
 * and Google Tag Manager (GTM) integration.
 *
 * Returns transformed feature flag data after processing Optimizely decision + cookies.
 * Automatically sends GTM events when flag is enabled and client is ready.
 *
 * @param featureFlag - The feature flag key to evaluate (e.g. "new-checkout-flow")
 * @param disableGTMEvent - Optional. If `true`, disables sending the GTM event even when the client is ready.
 * @param decideOptions - Optional. Array of Optimizely decide options to customize flag evaluation behavior.
 * @returns Transformed feature flag data (`TransformedFeatureFlag`) or an empty object (`Record<string, never>`) if not ready.
 *
 * @example
 * ```tsx
 * const { enabled } = useFeatureFlag('new-checkout')
 * ```
 *
 * @example
 * ```tsx
 * const { enabled } = useFeatureFlag('new-checkout', true, ['DISABLE_DECISION_EVENT'])
 * ```
 *
 * @remarks
 * - Uses Optimizely's `useDecide` for A/B testing and feature flag evaluation
 * - Transforms raw cookies alongside decision data using `transformFeatureFlag`
 * - Sends GTM events **only** when: client is ready + flag is enabled + not explicitly disabled
 * - GTM event can also be suppressed via the `disabledGTMEvent` field on the transformed flag data itself
 * - Dependencies: Optimizely SDK, custom cookie hook, GTM utility
 */
function useFeatureFlag(
  featureFlag: string,
  disableGTMEvent?: boolean,
  decideOptions: `${OptimizelyDecideOption}`[] = []
): TransformedFeatureFlag | Record<string, never> {
  const { decision, isLoading } = useDecide(featureFlag, {
    decideOptions: decideOptions as OptimizelyDecideOption[],
  })

  const { rawCookies } = useCookieState()

  const transformedCookies = Object.entries(rawCookies).map(([name, value]) => [
    name,
    { name, value },
  ])

  const featureFlagTransformedData = transformFeatureFlag(
    !isLoading ? decision : undefined,
    transformedCookies
  )

  useEffect(() => {
    if (disableGTMEvent || featureFlagTransformedData?.disabledGTMEvent) {
      return
    }

    if (isLoading || !decision) {
      return
    }

    sendFeatureFlagToGTM(featureFlag, decision.ruleKey || '', decision.variationKey || '')
  }, [
    isLoading,
    decision,
    featureFlag,
    disableGTMEvent,
    featureFlagTransformedData?.disabledGTMEvent,
  ])

  return featureFlagTransformedData
}

export default useFeatureFlag
